import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {useSiteNavigate} from "nav/SiteLink";
import {pricingCountryForContext} from "pricing/planCatalogue";
import {useLocale} from "i18n";
import {storefrontLocale} from "i18n/localePreference";
import {useAuthenticatedSession, useUser} from "UserProvider";
import {LimitWallOfferApi} from "./LimitWallOfferApi";
import {
	limitWallDialogOpen,
	limitWallPresentation,
	parseLimitWallToken,
	sessionSettledOnce,
	withoutLimitWallToken,
	type LimitWallPresentation,
	type LimitWallResolution,
} from "./limitWallOffer";

/** What `/pricing` needs in order to honour the limit-wall email link a reader arrived on. */
export interface LimitWallOfferState {
	presentation: LimitWallPresentation;
	/** The link's credential while the page still holds one; null once the link is dead. Never persisted. */
	token: string | null;
	/** The dialog is on screen. It stays mounted through checkout and drives the exit transition on close. */
	open: boolean;
	/** Whether this visit still wants the offer, read when a sign-in the Subscribe raised resumes. */
	activeVisit: () => boolean;
	/** The close button, the scrim and Escape. A this-visit fact only: nothing is stored. */
	dismiss: () => void;
	/** The till refused the token: withdraw the coupon and ask the api again what the link is worth now. */
	refusedAtTill: () => void;
}

/**
 * The pricing route's limit-wall adapter (kitecorp/transferify-api-service#2206).
 *
 * A thin React adapter: it reads the query, asks the api, holds the answer and this visit's three facts
 * (dismissed, refused at the till, the session has settled once), and asks `./limitWallOffer` what they
 * mean. It decides nothing.
 *
 * Shaped like `useLadderOffer` for that hook's reasons: the question is keyed on the storefront and the
 * token so a language switch is re-answered, an answer is kept only while its question is still the one
 * asked, the storefront is read inside the effect (it does not exist during the SSG pre-render), and the
 * dead link's parameter is dropped THROUGH THE ROUTER so a reload does not ask about it again.
 *
 * <b>A refusal re-asks once.</b> The api refuses a token at the till for reasons the page cannot see
 * (another account's link, the 72 hours running out, the day's redemptions spent between the page and
 * the press), and the re-read tells the page which of the full price or the expired notice to show.
 */
export function useLimitWallOffer(): LimitWallOfferState {
	const {locale} = useLocale();
	const location = useLocation();
	const navigate = useSiteNavigate();
	const [user] = useUser();
	const [session] = useAuthenticatedSession();
	const token = parseLimitWallToken(location.search);
	const [resolved, setResolved] = useState<LimitWallResolution | null>(null);
	const [refused, setRefused] = useState(false);
	const [dismissed, setDismissed] = useState(false);
	const [askCount, setAskCount] = useState(0);
	const [settledBefore, setSettledBefore] = useState(false);
	const asked = useRef<string | null>(null);
	const askedToken = useRef<string | null>(null);
	const mounted = useRef(true);
	useEffect(() => () => {
		mounted.current = false;
	}, []);

	useEffect(() => {
		const country = pricingCountryForContext(storefrontLocale(), window.location.hostname);
		const question = `${country ?? ""}:${token ?? ""}:${askCount}`;
		if (token === null || asked.current === question) {
			return;
		}
		asked.current = question;
		// A DIFFERENT link, for `useLadderOffer`'s reason: the previous link's answer and its refusal say
		// nothing about this one, so neither may show while this one is asked. Only the token clears them:
		// a re-ask after a refusal must keep the withdrawn coupon on screen, because no answer plus a
		// refusal reads as the expired notice.
		if (askedToken.current !== token) {
			askedToken.current = token;
			setResolved(null);
			setRefused(false);
		}
		void LimitWallOfferApi.resolve(token, country).then((resolution) => {
			if (mounted.current && asked.current === question) {
				setResolved(resolution);
			}
		});
		// `locale` is a change signal, as in `useLadderOffer`: the value used is read live above.
	}, [token, locale, askCount]);

	// Memoised so the dialog can hold the last claimable presentation by identity through its exit.
	const presentation = useMemo(() => limitWallPresentation(resolved, refused), [resolved, refused]);

	useEffect(() => {
		if (presentation.kind !== "expired" || parseLimitWallToken(location.search) === null) {
			return;
		}
		navigate(
			{pathname: location.pathname, search: withoutLimitWallToken(location.search), hash: location.hash},
			{replace: true},
		);
	}, [presentation.kind, navigate, location.pathname, location.search, location.hash]);

	// Latched during render (React's "storing information from previous renders"): it only ever turns true.
	const settled = sessionSettledOnce(settledBefore, session);
	if (settled !== settledBefore) setSettledBefore(settled);
	const open = limitWallDialogOpen(presentation, user, settled, dismissed);
	const openRef = useRef(open);
	openRef.current = open;
	const activeVisit = useCallback(() => openRef.current, []);
	const dismiss = useCallback(() => setDismissed(true), []);
	const refusedAtTill = useCallback(() => {
		setRefused(true);
		setAskCount(count => count + 1);
	}, []);

	// The token outlives its parameter only while the link is alive: the expired path drops both.
	return {presentation, token: presentation.kind === "expired" ? null : token, open, activeVisit, dismiss, refusedAtTill};
}
